/**
 * A filter chip for a vocabulary too large to hand the browser.
 *
 * Its single-select sibling takes the whole option list as a prop, which is
 * right for a status or a load type and wrong for anything a customer has
 * thousands of — a site-group picker that loads every group spends seconds and
 * megabytes to offer a list nobody scrolls. This one asks the server instead:
 * a page when the picker opens, a fresh first page when the typed text settles,
 * another page when the reader reaches the end of the list.
 *
 * Whatever is already chosen stays pinned at the top and selected even when no
 * loaded page contains it, so a narrowing arriving in a shared link can always
 * be read and undone.
 */
import FilterListIcon from '@mui/icons-material/FilterList';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';

import AsyncFilterOptionList from './AsyncFilterOptionList';
import useAsyncFilterOptions, { DEFAULT_PAGE_SIZE } from './useAsyncFilterOptions';

/**
 * @param {object} props
 * @param {string} props.label - the dimension being narrowed, e.g. "Plant".
 * @param {?string} [props.value] - the chosen option's value, or null for unset.
 * @param {(value: ?string) => void} props.onChange
 * @param {(query: { search: string, offset: number, limit: number, signal: AbortSignal })
 *   => Promise<{ options: { value: string, label: string }[], total: number }>} props.fetchPage
 *   - one page of options for the typed text. Must be referentially stable.
 * @param {?(value: string, context: { signal: AbortSignal })
 *   => Promise<?{ value: string, label: string }>} [props.resolveValue] - names the
 *   selection when no loaded page carries it. Omit when `selectedLabel` is known.
 * @param {?string} [props.selectedLabel] - the selection's label, when the caller has it.
 * @param {number} [props.pageSize]
 * @param {?string} [props.allLabel] - what the chip reads when nothing is chosen.
 * @param {?string} [props.searchPlaceholder]
 * @param {boolean} [props.disabled]
 * @param {?string} [props.chipLabel] - replaces the whole chip label.
 * @param {?string} [props.popoverTitle] - defaults to `label`.
 * @param {React.ReactNode} [props.icon]
 */
export default function DataGridAsyncSelectFilter({
  label,
  value = null,
  onChange,
  fetchPage,
  resolveValue = null,
  selectedLabel = null,
  pageSize = DEFAULT_PAGE_SIZE,
  allLabel = null,
  searchPlaceholder = null,
  renderOption = null,
  disabled = false,
  chipLabel = null,
  popoverTitle = null,
  icon: iconProp,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const state = useAsyncFilterOptions({
    active: isOpen,
    value,
    fetchPage,
    resolveValue,
    selectedLabel,
    pageSize,
  });
  const { selected, reset } = state;

  const handleOpen = useCallback(
    event => {
      reset();
      setAnchorEl(event.currentTarget);
    },
    [reset],
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    next => {
      onChange(next);
      setAnchorEl(null);
    },
    [onChange],
  );

  const resolvedAllLabel = allLabel ?? t('general.all', 'All');
  const chipState = selected?.label ?? resolvedAllLabel;

  return (
    <>
      <Chip
        icon={iconProp || <FilterListIcon sx={{ fontSize: 16 }} />}
        label={chipLabel ?? (label ? `${label}: ${chipState}` : chipState)}
        size="small"
        variant={value != null ? 'filled' : 'outlined'}
        onClick={handleOpen}
        disabled={disabled}
        sx={toolbarChipSx}
      />
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 280 } } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary' }}>
          {popoverTitle ?? label}
        </Typography>
        <AsyncFilterOptionList
          state={state}
          value={value}
          onSelect={handleSelect}
          allLabel={resolvedAllLabel}
          searchPlaceholder={searchPlaceholder}
          renderOption={renderOption}
        />
      </Popover>
    </>
  );
}
